// Keeping the Global timeline panel honest after a WRITE.
//
// The panel is a read-only projection of `GET timeline/global`, re-read on its own interval
// (globalTimeline.js REFRESH_MS = 5 minutes). Nothing else refetched it, so a task created due today
// — the SmartBox's most ordinary act — sat invisible in the panel for up to five minutes
// (busy-my-web, 2026-08-14). Rather than sprinkling `dispatch('globalTimeline/fetch')` through the
// 3.9k-line SmartBox and every store that writes, ONE store plugin listens for the actions that can
// change a row and refetches once they resolve.
//
// Only feed-affecting actions are listed: the feed renders tasks, events and waiting-on emails
// (BFF GlobalTimelineBuilder builds exactly those three), so note writes are deliberately NOT here —
// a note can never produce, remove or redate a row, and refetching for one would be a request that
// can only ever return what the panel already shows. Ongoing writes are not here either: `ongoing/remove`
// already does its own strip + reconcile (busy-my-web#55) and the other ongoing actions only move
// story steps, which this panel does not render.

// Store actions whose result can add, remove or move a row in the panel.
export const REFRESHING_ACTIONS = [
  // Tasks: created / edited (due date, title) / status-changed (done, someday, waiting-on) / removed.
  'task/addTask',
  'task/editTask',
  'task/updateStatus',
  'task/deleteTask',
  'task/restoreTasks',
  'task/deleteForever',
  // Events: today's schedule rows.
  'event/addEvent',
  'event/editEvent',
  'event/deleteEvent',
  // Emails: the waiting-on flag AND its due date are what put a thread in the feed.
  'timeline/toggleWaitingOn',
]

const WATCHED = new Set(REFRESHING_ACTIONS)

// Whether an action type means the panel is now out of date. Pure — the plugin below is a thin wrapper.
export function refreshesGlobalTimeline(type) {
  return typeof type === 'string' && WATCHED.has(type)
}

// How long to wait before refetching. A single SmartBox submit can fire several watched actions
// (create the task, then patch it), and the BFF drops its cached feed inside each write — so one
// coalesced read AFTER the last of them is both fewer requests and fresher data.
export const COALESCE_MS = 250

/**
 * Vuex plugin: refetch the Global timeline once, shortly after any write that can change it.
 *
 * Does nothing until the panel has loaded at least once (`globalTimeline/loaded`) — a user who never
 * opens the side panel must not pay for a feed they are not looking at.
 */
export function createGlobalTimelineRefreshPlugin({ delay = COALESCE_MS } = {}) {
  return (store) => {
    let pending = null

    store.subscribeAction({
      after: (action) => {
        if (!refreshesGlobalTimeline(action?.type)) return
        if (!store.getters['globalTimeline/loaded']) return
        if (pending) clearTimeout(pending)
        pending = setTimeout(() => {
          pending = null
          store.dispatch('globalTimeline/fetch')
        }, delay)
      },
    })
  }
}
